import React, { useState, useEffect } from 'react'
import { SettingsPageTemplate, SettingsCard, InfoTip } from '../components/SettingsPageTemplate'
import { PlusIcon, TrashIcon } from '../components/Icons'
import { API_BASE_URL } from '../utils/api'
import { useTranslation } from 'react-i18next'

interface TimeSlot {
  id: string
  start: string
  end: string
}

interface DaySchedule {
  isOpen: boolean
  slots: TimeSlot[]
}

interface WeeklySchedule {
  monday: DaySchedule
  tuesday: DaySchedule
  wednesday: DaySchedule
  thursday: DaySchedule
  friday: DaySchedule
  saturday: DaySchedule
  sunday: DaySchedule
}

interface VacationPeriod {
  id: string
  name: string
  startDate: string
  endDate: string
  isRecurring: boolean
  recurrenceType?: 'yearly' | 'monthly'
}

interface ScheduleSettings {
  weeklySchedule: WeeklySchedule
  timezone: string
  vacationPeriods: VacationPeriod[]
  bookingBuffer: number // Minutes between appointments
  maxAdvanceBooking: number // Days in advance clients can book
  minAdvanceBooking: number // Hours in advance clients can book
}

const ScheduleSettings: React.FC = () => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null)
  
  const defaultDaySchedule: DaySchedule = {
    isOpen: false,
    slots: [{ id: '1', start: '09:00', end: '17:00' }]
  }

  const [schedule, setSchedule] = useState<ScheduleSettings>({
    weeklySchedule: {
      monday: { isOpen: true, slots: [{ id: '1', start: '09:00', end: '17:00' }] },
      tuesday: { isOpen: true, slots: [{ id: '1', start: '09:00', end: '17:00' }] },
      wednesday: { isOpen: true, slots: [{ id: '1', start: '09:00', end: '17:00' }] },
      thursday: { isOpen: true, slots: [{ id: '1', start: '09:00', end: '17:00' }] },
      friday: { isOpen: true, slots: [{ id: '1', start: '09:00', end: '17:00' }] },
      saturday: { isOpen: false, slots: [{ id: '1', start: '09:00', end: '17:00' }] },
      sunday: { isOpen: false, slots: [{ id: '1', start: '09:00', end: '17:00' }] }
    },
    timezone: 'America/New_York',
    vacationPeriods: [],
    bookingBuffer: 15,
    maxAdvanceBooking: 60,
    minAdvanceBooking: 2
  })

  const [newVacation, setNewVacation] = useState({
    name: '',
    startDate: '',
    endDate: '',
    isRecurring: false,
    recurrenceType: 'yearly' as 'yearly' | 'monthly'
  })

  const [showVacationForm, setShowVacationForm] = useState(false)

  const daysOfWeek: (keyof WeeklySchedule)[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
  
  const getDayLabel = (day: keyof WeeklySchedule) => {
    return t(`settings.scheduleSettings.days.${day}`)
  }

  const timezones = [
    'America/New_York',
    'America/Chicago', 
    'America/Denver',
    'America/Los_Angeles',
    'America/Toronto',
    'America/Vancouver',
    'Europe/London',
    'Europe/Paris',
    'Asia/Tokyo'
  ]

  useEffect(() => {
    loadScheduleSettings()
  }, [])

  const loadScheduleSettings = async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('token')
      if (!token) return

      const response = await fetch(`${API_BASE_URL}/api/settings/schedule`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (response.ok) {
        const data = await response.json()
        setSchedule(data)
      }
    } catch (error) {
      console.error('Error loading schedule settings:', error)
    } finally {
      setLoading(false)
    }
  }

  const saveScheduleSettings = async () => {
    setSaving(true)
    try {
      const token = localStorage.getItem('token')
      if (!token) return

      const response = await fetch(`${API_BASE_URL}/api/settings/schedule`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(schedule)
      })

      if (response.ok) {
        setMessage({ type: 'success', text: t('settings.scheduleSettings.savedSuccess') })
      } else {
        throw new Error('Failed to save schedule settings')
      }
    } catch (error) {
      console.error('Error saving schedule settings:', error)
      setMessage({ type: 'error', text: t('settings.scheduleSettings.savedError') })
    } finally {
      setSaving(false)
    }
  }

  const updateDaySchedule = (day: keyof WeeklySchedule, updates: Partial<DaySchedule>) => {
    setSchedule(prev => ({
      ...prev,
      weeklySchedule: {
        ...prev.weeklySchedule,
        [day]: { ...prev.weeklySchedule[day], ...updates }
      }
    }))
  }

  const updateTimeSlot = (day: keyof WeeklySchedule, slotId: string, updates: Partial<TimeSlot>) => {
    const daySchedule = schedule.weeklySchedule[day]
    const updatedSlots = daySchedule.slots.map(slot => 
      slot.id === slotId ? { ...slot, ...updates } : slot
    )
    
    updateDaySchedule(day, { slots: updatedSlots })
  }

  const addTimeSlot = (day: keyof WeeklySchedule) => {
    const daySchedule = schedule.weeklySchedule[day]
    const newSlot: TimeSlot = {
      id: Date.now().toString(),
      start: '09:00',
      end: '17:00'
    }
    
    updateDaySchedule(day, { slots: [...daySchedule.slots, newSlot] })
  }

  const removeTimeSlot = (day: keyof WeeklySchedule, slotId: string) => {
    const daySchedule = schedule.weeklySchedule[day]
    if (daySchedule.slots.length > 1) {
      const updatedSlots = daySchedule.slots.filter(slot => slot.id !== slotId)
      updateDaySchedule(day, { slots: updatedSlots })
    }
  }

  const addVacationPeriod = () => {
    if (!newVacation.name || !newVacation.startDate || !newVacation.endDate) return

    const vacation: VacationPeriod = {
      id: Date.now().toString(),
      name: newVacation.name,
      startDate: newVacation.startDate,
      endDate: newVacation.endDate,
      isRecurring: newVacation.isRecurring,
      recurrenceType: newVacation.isRecurring ? newVacation.recurrenceType : undefined
    }

    setSchedule(prev => ({
      ...prev,
      vacationPeriods: [...prev.vacationPeriods, vacation]
    }))

    setNewVacation({
      name: '',
      startDate: '',
      endDate: '',
      isRecurring: false,
      recurrenceType: 'yearly'
    })
    setShowVacationForm(false)
  }

  const removeVacationPeriod = (vacationId: string) => {
    setSchedule(prev => ({
      ...prev,
      vacationPeriods: prev.vacationPeriods.filter(v => v.id !== vacationId)
    }))
  }

  const copyScheduleFromDay = (fromDay: keyof WeeklySchedule, toDay: keyof WeeklySchedule) => {
    const sourceSchedule = schedule.weeklySchedule[fromDay]
    const newSlots = sourceSchedule.slots.map(slot => ({
      ...slot,
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9)
    }))
    
    updateDaySchedule(toDay, {
      isOpen: sourceSchedule.isOpen,
      slots: newSlots
    })
    
    setMessage({ type: 'info', text: t('settings.scheduleSettings.copiedSchedule', { from: getDayLabel(fromDay), to: getDayLabel(toDay) }) })
  }

  return (
    <SettingsPageTemplate
      title={t('settings.scheduleSettings.title')}
      description={t('settings.scheduleSettings.description')}
      message={message}
      loading={loading}
    >
      {/* Weekly Schedule */}
      <SettingsCard title={t('settings.scheduleSettings.weeklyAvailability')} className="mb-6">
        <InfoTip type="info">
          {t('settings.scheduleSettings.weeklyAvailabilityTip')}
        </InfoTip>

        <div className="space-y-6">
          {daysOfWeek.map(day => {
            const daySchedule = schedule.weeklySchedule[day]
            return (
              <div key={day} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id={`${day}-enabled`}
                        checked={daySchedule.isOpen}
                        onChange={(e) => updateDaySchedule(day, { isOpen: e.target.checked })}
                        className="w-4 h-4 text-gray-900 bg-gray-100 border-gray-300 rounded focus:ring-gray-900 dark:focus:ring-gray-900 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                      />
                      <label htmlFor={`${day}-enabled`} className="ml-2 text-lg font-medium text-gray-900 dark:text-white">
                        {getDayLabel(day)}
                      </label>
                    </div>
                    {!daySchedule.isOpen && (
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {t('settings.scheduleSettings.closed')}
                      </span>
                    )}
                  </div>
                  
                  {daySchedule.isOpen && (
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => addTimeSlot(day)}
                        className="text-sm text-gray-900 hover:text-blue-800 dark:text-gray-600 dark:hover:text-blue-200"
                      >
                        {t('settings.scheduleSettings.addHours')}
                      </button>
                      <select
                        onChange={(e) => {
                          if (e.target.value) {
                            copyScheduleFromDay(e.target.value as keyof WeeklySchedule, day)
                            e.target.value = '' // Reset select
                          }
                        }}
                        className="text-sm px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        defaultValue=""
                      >
                        <option value="">{t('settings.scheduleSettings.copyFrom')}</option>
                        {daysOfWeek.filter(d => d !== day && schedule.weeklySchedule[d].isOpen).map(d => (
                          <option key={d} value={d}>{getDayLabel(d)}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                {daySchedule.isOpen && (
                  <div className="space-y-3">
                    {daySchedule.slots.map((slot, index) => (
                      <div key={slot.id} className="flex items-center space-x-3">
                        <div className="flex items-center space-x-2">
                          <input
                            type="time"
                            value={slot.start}
                            onChange={(e) => updateTimeSlot(day, slot.id, { start: e.target.value })}
                            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                          />
                          <span className="text-gray-500 dark:text-gray-400">{t('settings.scheduleSettings.to')}</span>
                          <input
                            type="time"
                            value={slot.end}
                            onChange={(e) => updateTimeSlot(day, slot.id, { end: e.target.value })}
                            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                          />
                        </div>
                        
                        {daySchedule.slots.length > 1 && (
                          <button
                            onClick={() => removeTimeSlot(day, slot.id)}
                            className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-200 p-1"
                            title={t('settings.scheduleSettings.removeTimeSlot')}
                          >
                            <TrashIcon size={16} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </SettingsCard>

      {/* Vacation Periods */}
      <SettingsCard title={t('settings.scheduleSettings.vacationTitle')} className="mb-6">
        <InfoTip type="warning">
          {t('settings.scheduleSettings.vacationTip')}
        </InfoTip>

        {schedule.vacationPeriods.length > 0 && (
          <div className="mb-6 space-y-3">
            {schedule.vacationPeriods.map(vacation => (
              <div key={vacation.id} className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-600 rounded-lg">
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white">
                    {vacation.name}
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {new Date(vacation.startDate).toLocaleDateString()} - {new Date(vacation.endDate).toLocaleDateString()}
                    {vacation.isRecurring && (
                      <span className="ml-2 px-2 py-1 bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200 text-xs rounded">
                        {t('settings.scheduleSettings.recurring')} {t(`settings.scheduleSettings.${vacation.recurrenceType}`)}
                      </span>
                    )}
                  </p>
                </div>
                <button
                  onClick={() => removeVacationPeriod(vacation.id)}
                  className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-200 p-1"
                  title={t('settings.scheduleSettings.removeVacationPeriod')}
                >
                  <TrashIcon size={16} />
                </button>
              </div>
            ))}
          </div>
        )}

        {!showVacationForm ? (
          <button
            onClick={() => setShowVacationForm(true)}
            className="flex items-center px-4 py-2 text-gray-900 hover:text-blue-800 dark:text-gray-600 dark:hover:text-blue-200 border border-blue-200 dark:border-gray-900 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
          >
            <PlusIcon className="mr-2" size={20} />
            {t('settings.scheduleSettings.addVacationPeriod')}
          </button>
        ) : (
          <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
            <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              {t('settings.scheduleSettings.addVacationPeriod')}
            </h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('settings.scheduleSettings.name')}
                </label>
                <input
                  type="text"
                  value={newVacation.name}
                  onChange={(e) => setNewVacation({...newVacation, name: e.target.value})}
                  placeholder={t('settings.scheduleSettings.namePlaceholder')}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                />
              </div>
              
              <div className="flex items-end">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={newVacation.isRecurring}
                    onChange={(e) => setNewVacation({...newVacation, isRecurring: e.target.checked})}
                    className="w-4 h-4 text-gray-900 bg-gray-100 border-gray-300 rounded focus:ring-gray-900"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                    {t('settings.scheduleSettings.recurringAnnually')}
                  </span>
                </label>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('settings.scheduleSettings.startDate')}
                </label>
                <input
                  type="date"
                  value={newVacation.startDate}
                  onChange={(e) => setNewVacation({...newVacation, startDate: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('settings.scheduleSettings.endDate')}
                </label>
                <input
                  type="date"
                  value={newVacation.endDate}
                  onChange={(e) => setNewVacation({...newVacation, endDate: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowVacationForm(false)
                  setNewVacation({
                    name: '',
                    startDate: '',
                    endDate: '',
                    isRecurring: false,
                    recurrenceType: 'yearly'
                  })
                }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                {t('settings.scheduleSettings.cancel')}
              </button>
              <button
                onClick={addVacationPeriod}
                disabled={!newVacation.name || !newVacation.startDate || !newVacation.endDate}
                className="px-4 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('settings.scheduleSettings.addVacationPeriod')}
              </button>
            </div>
          </div>
        )}
      </SettingsCard>

      {/* Booking Preferences */}
      <SettingsCard title={t('settings.scheduleSettings.bookingPreferences')} className="mb-6">
        <InfoTip type="info">
          {t('settings.scheduleSettings.bookingPreferencesTip')}
        </InfoTip>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('settings.scheduleSettings.timezone')}
            </label>
            <select
              value={schedule.timezone}
              onChange={(e) => setSchedule(prev => ({ ...prev, timezone: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
            >
              {timezones.map(tz => (
                <option key={tz} value={tz}>{tz.replace('_', ' ')}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('settings.scheduleSettings.bufferTime')}
            </label>
            <input
              type="number"
              min="0"
              max="120"
              value={schedule.bookingBuffer}
              onChange={(e) => setSchedule(prev => ({ ...prev, bookingBuffer: parseInt(e.target.value) || 0 }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {t('settings.scheduleSettings.bufferTimeHelp')}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('settings.scheduleSettings.maxAdvanceBooking')}
            </label>
            <input
              type="number"
              min="1"
              max="365"
              value={schedule.maxAdvanceBooking}
              onChange={(e) => setSchedule(prev => ({ ...prev, maxAdvanceBooking: parseInt(e.target.value) || 30 }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {t('settings.scheduleSettings.maxAdvanceBookingHelp')}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('settings.scheduleSettings.minAdvanceBooking')}
            </label>
            <input
              type="number"
              min="0"
              max="72"
              value={schedule.minAdvanceBooking}
              onChange={(e) => setSchedule(prev => ({ ...prev, minAdvanceBooking: parseInt(e.target.value) || 2 }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {t('settings.scheduleSettings.minAdvanceBookingHelp')}
            </p>
          </div>
        </div>
      </SettingsCard>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={saveScheduleSettings}
          disabled={saving}
          className="px-6 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {saving ? t('settings.scheduleSettings.saving') : t('settings.scheduleSettings.saveScheduleSettings')}
        </button>
      </div>
    </SettingsPageTemplate>
  )
}

export default ScheduleSettings
